'use client';

import { Heading, Icon, Modal, ModalBody, ModalFooter, ModalHeader } from '@petsmart-ui/sparky';

import type { AccountAddressesSynced } from '@libs/common-next';
import { useRemoveAddress } from '@libs/shared-ui';

import { GenericButton } from '../GenericButton';
import { trackErrorAddress, trackRemoveAddress } from './utils';

import './RemoveAddressModal.scss';

const componentClassname = 'remove-address-modal';

type RemoveAddressModalProps = {
    syncedAddresses: AccountAddressesSynced[] | undefined;
    isActive: boolean;
    isFraudValidationNeeded?: boolean;
    closeModal: () => void;
    addressId: number | undefined;
    closeForm: () => void;
};

export function RemoveAddressModal({
    syncedAddresses,
    isActive,
    closeModal,
    addressId,
    isFraudValidationNeeded = false,
    closeForm,
}: Readonly<RemoveAddressModalProps>) {
    const defaultAddress = syncedAddresses?.find(a => a.isPrimary);
    const selectedAddress = syncedAddresses?.find(a => a.addressId === addressId);
    const { currentStep, isLoading, REMOVE_STEP, handleRemoveAddress, setCurrentStep } = useRemoveAddress({
        defaultAddress,
        addresses: syncedAddresses,
        order: undefined,
        addressToRemove: selectedAddress ?? undefined,
        isFraudValidationNeeded,
        handleDrawerNavigation: {
            close: closeForm,
            back: closeModal,
            navigate: () => {},
        },
        hasSuccessHandler: true,
        onUpdate: () => Promise.resolve(closeForm()),
        handleBack: closeModal,
        onRemoveAddressSuccess: trackRemoveAddress,
        onRemoveAddressError: errorMessage => trackErrorAddress(errorMessage),
    });

    return (
        <Modal dismissible isActive={isActive} className={componentClassname} onClose={closeModal}>
            <ModalHeader data-testid="modal-header" closeButtonText="close">
                <Heading tagName="h5" className={`${componentClassname}__heading`}>
                    Remove address
                </Heading>
                <button
                    className="sparky-c-button sparky-c-modal__close-button sparky-c-button--bare sparky-c-button--circle"
                    aria-pressed="true"
                    type="button"
                    onClick={() => {
                        closeModal();
                    }}
                    data-testid="modal-close-btn"
                >
                    <Icon name="x" />
                </button>
            </ModalHeader>

            <ModalBody data-testid="modal-body" className={`${componentClassname}__body`}>
                <div className={`${componentClassname}__non-removable--text`}>
                    {!!REMOVE_STEP[currentStep].title && (
                        <p className={`${componentClassname}__non-removable--subtitle`}>
                            {REMOVE_STEP[currentStep].title}
                        </p>
                    )}
                    <p>
                        {currentStep === 'linkedConfirmation' && isFraudValidationNeeded
                            ? 'This will remove the address from your account. Your Autoship orders will continue using this address.'
                            : REMOVE_STEP[currentStep].description}
                    </p>
                </div>
            </ModalBody>

            <ModalFooter data-testid="modal-footer" className={`${componentClassname}__footer`} alignEnd>
                {!!REMOVE_STEP[currentStep].cancelButton && (
                    <GenericButton
                        className={`${componentClassname}__keep-cta`}
                        size="lg"
                        text={isFraudValidationNeeded ? 'Cancel' : 'Keep address'}
                        onClick={() => {
                            closeModal();
                            setCurrentStep('initialStep');
                        }}
                    />
                )}
                <GenericButton
                    data-testid="remove-address-cta"
                    loading={isLoading}
                    disabled={isLoading}
                    onClick={async () => {
                        await handleRemoveAddress(currentStep);
                    }}
                    className={currentStep === 'linkedConfirmation' ? `${componentClassname}__remove-cta` : ''}
                    variant="primary"
                    size="lg"
                    text={REMOVE_STEP[currentStep].modalActionButtonText}
                />
            </ModalFooter>
        </Modal>
    );
}
